/**
 * Zod validation schemas for stage layout data
 * Provides runtime validation and type checking for diagram_data JSON
 */

import { z } from 'zod';

// ============================================================================
// Canvas Configuration
// ============================================================================

export const CanvasConfigSchema = z.object({
  width_m: z.number().positive(),
  height_m: z.number().positive(),
  grid_size_m: z.number().positive(),
});

export type CanvasConfigType = z.infer<typeof CanvasConfigSchema>;

// ============================================================================
// Vector Types
// ============================================================================

export const Vec3Schema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

export type Vec3Type = z.infer<typeof Vec3Schema>;

// ============================================================================
// Stage Objects
// ============================================================================

export const StageObjectTypeSchema = z.enum([
  'paper_target',
  'steel_target',
  'popper',
  'plate',
  'swinger',
  'drop_turner',
  'texas_star',
  'bobber',
  'barrel',
  'table',
  'fault_line',
  'cone',
  'no_shoot',
  'hard_cover',
  'soft_cover',
  'shooter',
  'paper_swinger',
  'paper_swinger_once',
  'paper_popup',
  'chair',
  'door',
  'window',
  'bed',
  'couch',
  'car',
]);

export const ObjectVariantSchema = z.enum([
  'ipsc_classic',
  'ipsc_mini',
  'idpa',
  'uspsa_metric',
  'uspsa_mini',
  'round_8',
  'round_10',
  'round_12',
  'square',
  'rectangular',
  'standard',
  'mini',
  'half',
  'stack_2',
  'stack_3',
  'low',
  'shooter_relaxed',
  'shooter_hands_up',
  'shooter_seated',
  'shooter_broom',
  'with_window',
  'prone',
]);

export const ScoringZoneSchema = z.enum(['full', 'partial_head', 'partial_body']);

export const ActivationTypeSchema = z.enum([
  'static',
  'swinger',
  'drop_turner',
  'bobber',
]);

export const ActivationConfigSchema = z.object({
  type: ActivationTypeSchema,
  timing_seconds: z.number().optional(),
  swing_arc_degrees: z.number().optional(),
  exposure_time: z.number().optional(),
  trigger: z.string().optional(),
});

export const StageObjectSchema = z.object({
  id: z.string().uuid(),
  type: StageObjectTypeSchema,
  variant: ObjectVariantSchema.optional(),
  position: Vec3Schema,
  rotation: Vec3Schema,
  scale: Vec3Schema,
  scoring_zone: ScoringZoneSchema.optional(),
  on_stand: z.boolean(),
  stand_height_m: z.number().min(0),
  activation: ActivationConfigSchema.optional(),
  group_id: z.string().uuid().optional(),
  label: z.string().optional(),
  points: z.number().int().optional(),
  locked: z.boolean(),
  facing_direction: z.number().min(0).max(360),
});

export type StageObjectType = z.infer<typeof StageObjectSchema>;

// ============================================================================
// Shooting Positions
// ============================================================================

export const ShootingPositionTypeSchema = z.enum([
  'start_position',
  'shooting_box',
  'free_fire_zone',
]);

export const StartConditionSchema = z.enum([
  'hands_relaxed',
  'hands_surrendered',
  'loaded_holstered',
  'table_start',
  'wrists_below_belt',
]);

export const ShootingPositionSchema = z.object({
  id: z.string().uuid(),
  type: ShootingPositionTypeSchema,
  position: Vec3Schema,
  width_m: z.number().positive(),
  depth_m: z.number().positive(),
  rotation: z.number(),
  label: z.string(),
  is_start: z.boolean(),
  start_condition: StartConditionSchema.optional(),
});

export type ShootingPositionType = z.infer<typeof ShootingPositionSchema>;

// ============================================================================
// Walls and Barriers
// ============================================================================

export const WallTypeSchema = z.enum([
  'wall',
  'barrel_stack',
  'mesh_fence',
  'vision_barrier',
  'berm',
  'port_wall',
]);

export const WallMaterialSchema = z.enum([
  'plywood',
  'metal',
  'mesh',
  'concrete',
]);

export const PortConfigSchema = z.object({
  height_m: z.number().positive(),
  width_m: z.number().positive(),
  offset_from_ground_m: z.number().min(0),
});

export const WallSchema = z.object({
  id: z.string().uuid(),
  type: WallTypeSchema,
  points: z.array(Vec3Schema).min(2),
  height_m: z.number().positive(),
  has_port: z.boolean(),
  port_config: PortConfigSchema.optional(),
  material: WallMaterialSchema,
});

export type WallType = z.infer<typeof WallSchema>;

// ============================================================================
// Movement Paths
// ============================================================================

export const MovementPathSchema = z.object({
  id: z.string().uuid(),
  waypoints: z.array(Vec3Schema).min(1),
  label: z.string().optional(),
});

export type MovementPathType = z.infer<typeof MovementPathSchema>;

// ============================================================================
// Annotations
// ============================================================================

export const AnnotationTypeSchema = z.enum([
  'text',
  'measurement',
  'angle',
  'north_arrow',
  'boundary',
]);

export const AnnotationSchema = z.object({
  id: z.string().uuid(),
  type: AnnotationTypeSchema,
  position: Vec3Schema,
  text: z.string().optional(),
  font_size: z.number().positive().optional(),
  rotation: z.number().optional(),
  points: z.array(Vec3Schema).optional(),
  value: z.number().optional(),
});

export type AnnotationType = z.infer<typeof AnnotationSchema>;

// ============================================================================
// Stage Metadata
// ============================================================================

export const DisciplineSchema = z.enum([
  'ipsc_handgun',
  'ipsc_rifle',
  'idpa',
  'uspsa',
  'steel_challenge',
]);

export const ScoringTypeSchema = z.enum([
  'comstock',
  'virginia_count',
  'fixed_time',
  'vickers_count',
  'limited_vickers',
]);

export const StageMetadataSchema = z.object({
  name: z.string().min(1),
  discipline: DisciplineSchema.optional(),
  scoring_type: ScoringTypeSchema.optional(),
  briefing: z.string().optional(),
  author_id: z.string().optional(),
  created_at: z.string().datetime().optional(),
  updated_at: z.string().datetime().optional(),
});

export type StageMetadataType = z.infer<typeof StageMetadataSchema>;

// ============================================================================
// Camera State
// ============================================================================

export const CameraStateSchema = z.object({
  position: Vec3Schema,
  target: Vec3Schema,
  zoom: z.number().positive(),
});

export type CameraStateType = z.infer<typeof CameraStateSchema>;

// ============================================================================
// Root Stage Layout
// ============================================================================

export const StageLayoutSchema = z.object({
  version: z.string(),
  canvas: CanvasConfigSchema,
  objects: z.array(StageObjectSchema),
  shooting_positions: z.array(ShootingPositionSchema),
  walls: z.array(WallSchema),
  movement_paths: z.array(MovementPathSchema),
  annotations: z.array(AnnotationSchema),
  metadata: StageMetadataSchema,
  camera: CameraStateSchema.optional(),
  thumbnail_url: z.string().url().optional(),
});

export type StageLayoutType = z.infer<typeof StageLayoutSchema>;

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates a stage layout against the schema
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateStageLayout(data: unknown): StageLayoutType {
  return StageLayoutSchema.parse(data);
}

/**
 * Safely validates a stage layout, returning errors instead of throwing
 * @param data - The data to validate
 * @returns A result object with either validated data or errors
 */
export function safeValidateStageLayout(
  data: unknown
): { success: true; data: StageLayoutType } | { success: false; errors: z.ZodError } {
  const result = StageLayoutSchema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, errors: result.error };
}

/**
 * Validates a single stage object
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateStageObject(data: unknown): StageObjectType {
  return StageObjectSchema.parse(data);
}

/**
 * Validates a single shooting position
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateShootingPosition(data: unknown): ShootingPositionType {
  return ShootingPositionSchema.parse(data);
}

/**
 * Validates a single wall
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateWall(data: unknown): WallType {
  return WallSchema.parse(data);
}

/**
 * Validates metadata
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateStageMetadata(data: unknown): StageMetadataType {
  return StageMetadataSchema.parse(data);
}

/**
 * Validates canvas configuration
 * @param data - The data to validate
 * @returns The validated data or throws a ZodError
 */
export function validateCanvasConfig(data: unknown): CanvasConfigType {
  return CanvasConfigSchema.parse(data);
}

/**
 * Gets a human-readable error message from a ZodError
 * @param error - The ZodError to format
 * @returns A formatted error message
 */
export function formatValidationErrors(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.join('.');
      return `${path || 'root'}: ${issue.message}`;
    })
    .join('\n');
}
